import { Dialog, Persona, Stack, Text, useDialogController, useViewport } from '@amx/ux-twoui';
import { useLocalization } from '@microsoft/portal-app/lib/shared/localization';
import { useTwoUIOperationDispatcher } from '@amx/ux-twoui-telemetry';
import { useCallback, useEffect } from 'react';
import React from 'react';
import { useRemovePhoto } from '../../hooks/useUpdateProfilePhoto';
import { EditPhotoDialog } from './EditPhotoDialog';
import { MOBILE_BREAK_POINT, PROFILE_PHOTO_ALLOWED_OPERATIONS } from '../../shared/constants';
import { useTelemetry } from '@iamexperiences/ecos-telemetry';

const operationName = 'deleting-profile-photo';

interface IRemovePhotoDialogProps {
  displayName: string;
  imageUrl: string;
}

export const RemovePhotoDialog: React.FC<IRemovePhotoDialogProps> = ({ displayName, imageUrl }) => {
  const removePhotoMutation = useRemovePhoto();

  const dialogController = useDialogController();
  const twoUIDispatcher = useTwoUIOperationDispatcher();
  const [t] = useLocalization('Account');
  const telemetry = useTelemetry();

  const viewportType = useViewport();
  const [error, setError] = React.useState<string | undefined>(undefined);

  useEffect(() => {
    if (removePhotoMutation.isSuccess) {
      dialogController.switchToDialog(
        <EditPhotoDialog displayName={displayName} allowedOperations={PROFILE_PHOTO_ALLOWED_OPERATIONS.update} />,
      );
      telemetry.reportCustomEvent('update-photo/remove', { message: 'successfully' });
      twoUIDispatcher({ name: operationName, status: 'finished' });
    }
    if (removePhotoMutation.isLoading) {
      twoUIDispatcher({ name: operationName, status: 'updating' });
    }
    if (removePhotoMutation.isError) {
      setError(t('editPhotoGenericError'));
      telemetry.error('update-photo/remove', removePhotoMutation.error);
      twoUIDispatcher({ name: operationName, status: 'failed' });
    }
  }, [
    removePhotoMutation.isSuccess,
    dialogController,
    removePhotoMutation.isLoading,
    twoUIDispatcher,
    displayName,
    removePhotoMutation.isError,
    t,
    removePhotoMutation.error,
    telemetry,
  ]);

  const onYes = useCallback(async () => {
    removePhotoMutation.mutate();
  }, [removePhotoMutation]);

  const onCancelClick = useCallback(() => {
    dialogController.switchToDialog(
      <EditPhotoDialog
        displayName={displayName}
        photoUrl={imageUrl}
        allowedOperations={`${PROFILE_PHOTO_ALLOWED_OPERATIONS.update}, ${PROFILE_PHOTO_ALLOWED_OPERATIONS.delete}`}
      />,
    );
  }, [dialogController, displayName, imageUrl]);

  return (
    <Dialog
      isOpen={true}
      closeButton={{
        'aria-label': t('close'),
        'data-bi-id': '',
      }}
      onDismiss={onCancelClick}
      dialogFooterProps={{
        buttonPrimary: {
          'aria-label': t('yes'),
          text: t('yes'),
          'data-bi-id': '',
          onClick: onYes,
          operationData: {
            progressViewOperationData: {
              componentName: 'remove-photo-dialog',
              operationName,
              defaultState: 'finished',
            },
          },
        },
        buttonSecondary: {
          'aria-label': t('no'),
          text: t('no'),
          'data-bi-id': '',
          onClick: onCancelClick,
          disabled: false,
        },
      }}
      staticBodyWidth={viewportType === MOBILE_BREAK_POINT ? undefined : '480px'}
      stickyHeaderFooter={{
        hasStickyHeaderFooters: true,
      }}
      title={t('removePhoto')}
    >
      <Stack childrenGap={10}>
        <Stack horizontalAlign="center">
          <Persona size="200" imageUrl={imageUrl} />
        </Stack>
        {error && <Text colorOverride="error">{error}</Text>}
        <Text>{t('removePhotoConfirmation')}</Text>
      </Stack>
    </Dialog>
  );
};
